// bfsg

import useTranslations from 'hooks/useTranslations'
import * as React from 'react'
import Button from 'theme/atoms/Button'
import { ReactComponent as CloseIcon } from 'assets/closeOutline.svg'
import { ReactComponent as CopyIcon } from 'assets/copy.svg'
import { pushTrackingEvent } from '../utils'
import { Props } from './types'
import { styled } from 'styled-components'
import theme from 'theme'

export default function ModalContent(props: Props) {
  const t = useTranslations<'molecules-Share'>()
  const modalRef = React.useRef<HTMLDialogElement>(null)

  const [showConfirmationForCopyField, setShowConfirmationForCopyField] =
    React.useState(false)
  const [showConfirmationForCopyButton, setShowConfirmationForCopyButton] =
    React.useState(false)

  // always close confirmation after two seconds
  React.useEffect(() => {
    if (!showConfirmationForCopyField) return

    const tid = setTimeout(() => setShowConfirmationForCopyField(false), 2000)
    return () => clearTimeout(tid)
  }, [showConfirmationForCopyField])

  React.useEffect(() => {
    if (!showConfirmationForCopyButton) return

    const tid = setTimeout(() => setShowConfirmationForCopyButton(false), 2000)
    return () => clearTimeout(tid)
  }, [showConfirmationForCopyButton])

  React.useEffect(() => {
    if (modalRef.current) {
      modalRef.current.addEventListener('close', () => {
        props.onCloseClick()
      })
    }
    if (modalRef.current?.showModal) modalRef.current?.showModal() // This method will not be available in the unit test mock.
  }, [])

  const handleCopyClick = () => {
    navigator.clipboard.writeText(props.linkToShare)

    pushTrackingEvent('submit', 'copy_url')

    setShowConfirmationForCopyButton(true)
  }

  // TODO: BFSG Snippet in line 67
  /* eslint-disable @kaminrunde/firescout/onclick-needs-handle */
  return (
    <Wrapper
      className="ModalContent"
      data-cy-state="share-modal-open"
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      ref={modalRef}
    >
      <div className="close-icon">
        <button title="close modal" onClick={props.onCloseClick}>
          <CloseIcon data-cy-handle="close-share-modal" />
        </button>
      </div>
      <div className="content">
        <h2>{t('modal_headline')}</h2>

        <p>{t('modal_info_text')}</p>

        <div
          className="copy_link_field"
          onClick={() => {
            handleCopyClick()
          }}
          data-cy-handle="copy-link-field"
        >
          <p>
            {showConfirmationForCopyField
              ? t('copy_link_confirmation')
              : props.linkToShare}
          </p>
          <button
            title="copy link to clipboard" // TODO: bfsg snippet
            onClick={() => {
              handleCopyClick()
            }}
          >
            <CopyIcon className="copy-icon" />
          </button>
        </div>

        <div className="modal_button_group">
          <Button
            variation="secondary"
            icon="link"
            onClick={handleCopyClick}
            data-cy-handle="copy-link-button"
          >
            <p>
              {showConfirmationForCopyButton
                ? t('copy_link_confirmation')
                : t('copy_link')}
            </p>
          </Button>
          <Button
            variation="secondary"
            icon="whatsapp"
            onClick={() => {
              const text = encodeURIComponent(`${props.linkToShare}`)
              const whatsappUrl = `https://wa.me/?text=${text}`

              window.open(whatsappUrl, '_blank')
              pushTrackingEvent('submit', 'whatsapp')
            }}
            data-cy-handle="share-link-by-whatsapp"
          >
            <p>{t('share_by_whatsapp')}</p>
          </Button>
          <Button
            variation="secondary"
            icon="e-mail"
            onClick={() => {
              const subject =
                t('share_by_email_subject').props.children +
                ' ' +
                props.productName

              const body = encodeURIComponent(`${props.linkToShare}`)

              window.location.href = `mailto:?subject=${subject}&body=${body}`
              pushTrackingEvent('submit', 'e-mail')
            }}
            data-cy-handle="share-link-by-mail"
          >
            <p>{t('share_by_email')}</p>
          </Button>
          <Button
            variation="text"
            onClick={props.onCloseClick}
            data-cy-handle="close-share-modal"
            className="modal_close_button"
          >
            <p>{t('modal_close')}</p>
          </Button>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.dialog`
  border-color: ${theme.colors.shade.b2};
  min-width: 21.875rem;
  background-color: ${theme.colors.white};
  padding: ${theme.spacing('s')};
  @media (min-width: ${theme.ms.XL}px) {
    padding: ${theme.spacing('l')};
  }
  overflow-y: scroll;

  max-height: 85%;
  > .close-icon {
    text-align: end;
    cursor: pointer;
    width: 100%;
  }
  > .content {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('s')};
    > .modal-headline {
      ${theme.ty('rc-xl', '700', 'r-2xl')}
      margin-bottom: ${theme.spacing('m')};
    }
    > .info-list {
      list-style: inside decimal;
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing('s')};
      > li {
        ${theme.ty('r-s', null, 'r-base')}
      }
    }

    > .copy_link_field {
      cursor: pointer;
      display: flex;
      align-items: center;
      border: 1px solid ${theme.colors.shade.b2};
      ${theme.rounding('m')}
      justify-content: space-between;
      background-color: ${theme.colors.shade.primaryUltraBright};
      ${theme.ty('r-base', '400')};
      ${theme._ty([16, 0, 24], theme.font, '400')}

      color: ${theme.colors.b0};
      margin-bottom: ${theme.spacing('s')};
      padding: ${theme.spacing('xxs')} ${theme.spacing('s')};
      word-break: break-all;

      > .copy-icon {
        min-width: 1.25rem;
      }
    }

    > .modal_button_group {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing('xs')};
      @media (min-width: ${theme.ms.XL}px) {
        flex-direction: row;
        justify-content: start;
      }

      > .modal_close_button {
        @media (min-width: ${theme.ms.XL}px) {
          display: none;
        }

        > p {
          text-decoration: underline;
        }
      }

      > .Button {
        border-color: ${theme.colors.shade.b2};
        > p {
          margin-top: 0;
          margin-bottom: 0;
        }
      }
    }
  }
`
